import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import Headers from "../components/Headers";
import { normalizeStoreName } from "../utils/storeUtils";
import Footer from "../components/Footer";
import ShopProducts from "../components/products/ShopProducts"; // Component to display products
import { useDispatch, useSelector } from "react-redux";
import { fetchProductsBySeller, fetchStoreBySlug, fetchStoreByDomain, setStoreCategoryFilter, setStoreSearchKeyword } from "../store/reducers/homeReducer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BadgeCheck, ChevronDown, LayoutGrid, LayoutList, Package, Search, X } from "lucide-react";
import ProductDetailsSkeleton from "./ProductDetailsSkeleton";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import api from '../api/api';
import SectionRenderer from "../components/sections/SectionRenderer";

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 1
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1
  }
};

const CustomDot = ({ onClick, ...rest }) => {
  const {
    onMove,
    index,
    active,
    carouselState: { currentSlide, deviceType }
  } = rest;
  return (
    <button
      className={`${active ? "bg-white w-4" : "bg-white/50 w-2 hover:bg-white/80"} h-2 rounded-full mx-0.5 transition-all duration-300`}
      onClick={() => onClick()}
    />
  );
};

const StoreDetails = () => {
  const { storeid, storeName, storename } = useParams(); // Extract the store ID or storeName from the URL
  const effectiveStoreName = storeName || storename;
  const { products, totalProducts, parPage, seller, storeSearchKeyword, storeCategoryFilter, loading } = useSelector(
    (state) => state.home
  );


  const [styles, setStyles] = useState("grid");
  const [sortPrice, setSortPrice] = useState("all");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [allProducts, setAllProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  // Toggle this to force verification badge display
  const FORCE_VERIFIED = false;
  const isVerified = FORCE_VERIFIED || seller.verification?.verified;

  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(1);

  // Products fetching effect... (kept as is)
  useEffect(() => {
    if (products) {
      const uniqueCategories = [...new Set(products.map(p => p.category))];
      setStoreCategories(uniqueCategories.map(c => ({ name: c })));
    }
  }, [products]);

  useEffect(() => {
    const host = window.location.host;
    // Check if we are on a custom domain (not localhost, not the main domain, not dashboard)
    const isCustomDomain = !host.includes('stores.mypursepay.com') &&
      !host.includes('localhost') &&
      !host.includes('dashboard.mypursepay.com') &&
      !host.includes('merchant.mypursepay.com');

    // StoreLayout handles the initial fetch for seller info, 
    // but here we might need to fetch *products* specifically if we have ID.
    // If StoreLayout fetched by slug, seller is populated.

    // We still need to fetch products by seller ID once seller is loaded.
    if (storeid) {
      // Direct access via /stores/:name/:id
      dispatch(fetchProductsBySeller({ sellerId: storeid, pageNumber }));
    } else if (seller && seller._id) {
      // Access via Slug or Custom Domain, and seller is loaded by Layout
      dispatch(fetchProductsBySeller({ sellerId: seller._id, pageNumber }));
    }
  }, [storeid, effectiveStoreName, pageNumber, dispatch, seller?._id]); // Added seller dependency

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveStoreName]);

  // Accumulate products logic... (kept as is)
  useEffect(() => {
    if (products) {
      if (products.length < 5) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (pageNumber === 1) {
        setAllProducts(products);
      } else {
        setAllProducts(prev => {
          const newProducts = products.filter(p => !prev.some(existing => existing._id === p._id));
          return [...prev, ...newProducts];
        });
      }
    }
  }, [products, pageNumber]);

  useEffect(() => {
    if (allProducts) {
      let tempProducts = [...allProducts];

      if (storeSearchKeyword) {
        tempProducts = tempProducts.filter(p =>
          p.name.toLowerCase().includes(storeSearchKeyword.toLowerCase())
        );
      }

      if (storeCategoryFilter) {
        tempProducts = tempProducts.filter(p =>
          p.category === storeCategoryFilter
        );
      }

      setFilteredProducts(tempProducts);
    }
  }, [allProducts, storeSearchKeyword, storeCategoryFilter]);

  const observer = useRef();
  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPageNumber(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);


  if (!seller || !seller.shopInfo) {
    return (
      <div className={`min-h-screen bg-[var(--store-bg)]`}>
        {/* Header managed by StoreLayout */}
        <ProductDetailsSkeleton />
        {/* Footer managed by StoreLayout */}
      </div>
    )
  }

  // Filter out header/footer from sections if they exist, so we don't double render
  // StoreLayout renders them wrapping this component.
  const contentSections = seller.layout ? seller.layout.filter(s => s.type !== 'header' && s.type !== 'footer') : [];

  return (
    <div className={`min-h-screen bg-[var(--store-bg)] transition-colors duration-300`}>
      {/* Render all sections in order, matching the builder/preview */}
      {/* If no layout, show banner/hero first, then fallback product grid */}
      {(!seller.layout || seller.layout.length === 0) ? (
        <>
          {/* Header managed by Layout */}
          {seller.banners && seller.banners.length > 0 ? (
            <div className="w-full h-[280px] relative">
              <Carousel
                responsive={responsive}
                infinite={true}
                autoPlay={true}
                autoPlaySpeed={3000}
                showDots={true}
                arrows={false}
                className="h-full"
                dotListClass="!bottom-4 !left-1/2 !-translate-x-1/2 !w-fit !bg-white/20 !backdrop-blur-md !border !border-white/20 !rounded-full !px-2 !py-1.5 !flex !gap-1 !items-center !z-10 !m-0"
                customDot={<CustomDot />}
              >
                {seller.banners.map((banner, index) => (
                  <div key={index} className="w-full h-[280px]">
                    <img src={banner} alt={`Banner ${index + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </Carousel>
            </div>
          ) : (
            <section
              style={{
                backgroundImage: `url(${seller.image || `${process.env.REACT_APP_URL}/images/storebannerdefault.jpg`})`,
                height: "280px",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
              className="relative w-full"
            >
            </section>
          )}
          {/* Fallback product grid after banner/hero */}
          <section className="py-5" id="store-products">
            <div className="w-[80%] md:w-[90%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
              <div className="w-12/12 md-lg:w-12/12 md:w-full">
                <div className="pl-8 md:pl-0">
                  <div className="lg-md:py-10 mb-10 rounded-md flex justify-between items-start">
                    <h2 className="text-lg font-medium text-[var(--store-text)]">
                      {totalProducts} Products
                    </h2>
                    <div className="flex justify-center items-center gap-3">
                      <div className="flex justify-center items-start gap-4 md-lg:hidden">
                        <div
                          onClick={() => setStyles("grid")}
                          className={`p-2 ${styles === "grid" && "bg-[var(--store-card-bg)]"
                            } text-[var(--store-text)] cursor-pointer rounded-sm`}
                        >
                          <LayoutGrid size={18} />
                        </div>
                        <div
                          onClick={() => setStyles("list")}
                          className={`p-2 ${styles === "list" && "bg-[var(--store-card-bg)]"
                            } text-[var(--store-text)] cursor-pointer rounded-sm`}
                        >
                          <LayoutList size={18} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pb-8">
                    <ShopProducts products={filteredProducts} styles={styles} storeMode={true} />
                  </div>
                  {/* Infinite Scroll Trigger */}
                  <div ref={lastProductElementRef} className="w-full flex justify-center py-4 flex-col items-center gap-2">
                    {loading && (
                      <div className="w-8 h-8 border-4 border-[var(--store-text)] border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {!loading && (allProducts.length >= totalProducts || !hasMore) && totalProducts > 0 && (
                      <span className="text-[var(--store-muted)] text-sm font-medium"></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Render content sections (excluding header/footer which are in Layout) */}
          <SectionRenderer sections={contentSections} sellerId={seller._id} seller={seller} />

          {/* Fallback product list if not in layout? */}
          {!seller.layout.some(s => s.type === 'store-product-list') && (
            <section className="py-5" id="store-products">
              <div className="w-[80%] md:w-[90%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
                <div className="w-12/12 md-lg:w-12/12 md:w-full">
                  <div className="pl-8 md:pl-0">
                    <div className="lg-md:py-10 mb-10 rounded-md flex justify-between items-start">
                      <h2 className="text-lg font-medium text-[var(--store-text)]">
                        {totalProducts} Products
                      </h2>
                      <div className="flex justify-center items-center gap-3">
                        <div className="flex justify-center items-start gap-4 md-lg:hidden">
                          <div
                            onClick={() => setStyles("grid")}
                            className={`p-2 ${styles === "grid" && "bg-[var(--store-card-bg)]"
                              } text-[var(--store-text)] cursor-pointer rounded-sm`}
                          >
                            <LayoutGrid size={18} />
                          </div>
                          <div
                            onClick={() => setStyles("list")}
                            className={`p-2 ${styles === "list" && "bg-[var(--store-card-bg)]"
                              } text-[var(--store-text)] cursor-pointer rounded-sm`}
                          >
                            <LayoutList size={18} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pb-8">
                      <ShopProducts products={filteredProducts} styles={styles} storeMode={true} />
                    </div>
                    {/* Infinite Scroll Trigger */}
                    <div ref={lastProductElementRef} className="w-full flex justify-center py-4 flex-col items-center gap-2">
                      {loading && (
                        <div className="w-8 h-8 border-4 border-[var(--store-text)] border-t-transparent rounded-full animate-spin"></div>
                      )}
                      {!loading && (allProducts.length >= totalProducts || !hasMore) && totalProducts > 0 && (
                        <span className="text-[var(--store-muted)] text-sm font-medium"></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {seller?.shopInfo?.whatsappNumber && (
        <>
          <style>
            {`
              .whatsapp-float-btn {
                position: fixed;
                right: 1.5rem;
                z-index: 50;
                transition: transform 300ms;
                bottom: 6rem; /* Mobile default: bottom-24 to clear floating cart */
              }
              .whatsapp-float-btn:hover {
                transform: scale(1.1);
              }
              @media (min-width: 768px) {
                .whatsapp-float-btn {
                  bottom: 1.5rem; /* Desktop: bottom-6 */
                }
              }
            `}
          </style>
          <a
            href={`https://wa.me/${seller.shopInfo.whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-float-btn"
          >
            <img
              src="/images/whatsapp.png" // Assumes this exists or use a variable
              alt="Chat on WhatsApp"
              className="w-14 h-14 drop-shadow-lg"
            />
          </a>
        </>
      )}
      {/* Footer managed by Layout */}
    </div>
  );
};

export default StoreDetails;
